<template>
  <div
    class="config-wrapper config-box"
    :class="{ active: showConfig[question_result_index] }"
  >
    <component
      :is="getComponent()"
      :id="`group-${groupCode}-questionResult-${question_result_index}`"
      :question_result="question_result"
      :groupCode="groupCode"
      :defaultTheme="defaultTheme"
    />

    <div class="chart-config-buttons">
      <button @click="cancel">{{ parseTranslation(CANCEL) }}</button>

      <button @click="save">
        <Loader v-if="loading" :size="16" />

        <span v-else> {{ parseTranslation(SAVE) }} </span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import Loader from '@/components/loading/Loader-3'

import { CHART_TYPES } from '../../Charts/chartConfig'

import ChartConfigs from './ChartConfigs'
import WordCloudConfig from '../ChartTypes/WordCloud/WordCloudConfig.vue'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

export default {
  name: 'Config',

  components: {
    ChartConfigs,
    WordCloudConfig,
    Loader,
  },

  props: {
    question_result: {
      required: true,
      type: Object,
    },

    groupCode: {
      required: true,
    },

    defaultTheme: {
      required: true,
    },

    question_result_index: {
      required: true,
    },
  },

  computed: {
    ...mapGetters(['showConfig']),
  },

  data() {
    return {
      loading: false,

      translations,
      ...translationKeys,
    }
  },

  methods: {
    ...mapActions(['setShowConfig', 'executeSurveyAPICall', 'sendMessage']),

    cancel() {
      const index = this.question_result_index

      this.setShowConfig({
        ...this.showConfig,
        [index]: !this.showConfig[index],
      })
    },

    async save() {
      if (this.loading) return

      this.loading = true

      const surveyId = this.$route.query?.survey

      const { code, displayAsChart, chartSettings } = this.question_result

      const body = {
        surveyId,
        questionGroup: this.groupCode,
        questionCode: code,

        config: {
          displayAsChart: displayAsChart,
          chartSettings: chartSettings,
        },
      }

      const surveyUserConfig = await this.executeSurveyAPICall({
        method: 'POST',
        path: 'report/survey-user-config',
        payload: body,
      })

      if (!surveyUserConfig) {
        this.loading = false

        return
      }

      this.sendMessage(this.parseTranslation(this.SAVED_SUCCESS))

      this.loading = false
    },

    getComponent() {
      const resultType = this.question_result.resultType

      switch (resultType) {
        case CHART_TYPES.WORD_CLOUD:
          return 'WordCloudConfig'
        default:
          return 'ChartConfigs'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.config-wrapper {
  transition: all 0.5s ease-in-out;
  display: none;

  &.active {
    display: block;
    animation: growDown 400ms ease-in-out forwards;
    transform-origin: top center;
  }

  .chart-config-buttons {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;

    button {
      @include transparentButton;
      width: 110px;
      padding: 8px 15px;
      @include tipography(14);
      font-weight: 600;
      letter-spacing: 0.15em;
      text-transform: uppercase;
      border: 1px solid $blue;
      border-radius: 3px;
      background: $blue;
      box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.15);

      &:hover {
        background: $blue-2;
        transform: scale(1.01);
      }

      &:first-of-type {
        margin-right: 20px;
        color: $pink;
        border: 1px solid $pink;
        background: transparent;

        &:hover {
          transform: scale(1.01);
        }
      }

      &:last-of-type {
        @include flexMiddle;
      }
    }

    @media (max-width: 380px) {
      flex-direction: column;
      align-items: center;

      button {
        &:first-of-type {
          margin: 0 0 20px 0;
        }
      }
    }
  }
}
</style>
